import { VehicleListItem, VehicleListResponse } from 'models'
import { z } from 'zod'
import { parseWithSchema } from './zodValidation'

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return null
}

const toNullableBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') {
      return true
    }
    if (normalized === 'false') {
      return false
    }
  }

  return null
}

const nullableStringSchema = z.string().nullable().default(null).catch(null)
const nullableUnknownSchema = z.unknown().nullable().default(null).catch(null)
const nullableNumberSchema = z
  .union([z.number(), z.string(), z.null(), z.undefined()])
  .transform((value) => toNullableNumber(value))
  .default(null)
  .catch(null)
const nullableBooleanSchema = z
  .union([z.boolean(), z.string(), z.null(), z.undefined()])
  .transform((value) => toNullableBoolean(value))
  .default(null)
  .catch(null)

const nonNegativeCoercedIntSchema = z.coerce.number().int().min(0)
const positiveCoercedIntSchema = z.coerce.number().int().min(1)

const vehicleListVehiclePartialSchema = z
  .object({
    licensePlate: nullableStringSchema,
    vin: nullableStringSchema,
    make: nullableStringSchema,
    engineTrade: nullableStringSchema,
    dateOfFirstRegistration: nullableStringSchema,
    color: nullableStringSchema,
    typeRegDoc: nullableStringSchema,
    fuelType: nullableStringSchema,
    type: nullableStringSchema.optional().default(null).catch(null),
    dateFirstReg: nullableStringSchema.optional().default(null).catch(null),
  })
  .passthrough()
  .nullable()
  .default(null)
  .catch(null)

const vehicleListVehicleDetailSchema = z
  .object({
    id: nullableStringSchema,
    version: nullableStringSchema,
  })
  .passthrough()
  .nullable()
  .default(null)
  .catch(null)

const vehicleListInformationSchema = z
  .object({
    fre: nullableNumberSchema,
    observationsVendeur: nullableStringSchema,
    nbPassage: nullableNumberSchema,
    createdOn: nullableStringSchema,
    arriveDate: nullableStringSchema,
    stockInElapsedDays: nullableNumberSchema,
    nextAuctionStartTime: nullableStringSchema,
    isRegistrationDocumentReceived: nullableBooleanSchema,
    passingOrderNo: nullableNumberSchema,
    mileage: nullableNumberSchema.optional(),
  })
  .passthrough()
  .nullable()
  .default(null)
  .catch(null)

const vehicleListPricingSchema = z
  .object({
    coteArgusPriceDate: nullableNumberSchema,
    coteArgusKm: nullableNumberSchema,
    tvaRecuperable: nullableBooleanSchema,
    withdrawalPrice: nullableNumberSchema,
    bidPrice: nullableNumberSchema,
    reserveTTCHF: nullableNumberSchema.optional(),
    estimationApporteur: nullableNumberSchema.optional(),
  })
  .passthrough()
  .nullable()
  .default(null)
  .catch(null)

const vehicleListStatusSchema = z
  .object({
    logisticStatus: nullableNumberSchema,
    generalStatus: nullableNumberSchema,
    blockingReason: nullableStringSchema,
  })
  .passthrough()
  .nullable()
  .default(null)
  .catch(null)

const vehicleListVendorInformationSchema = z
  .object({
    referenceVendeur: nullableStringSchema,
  })
  .passthrough()
  .nullable()
  .default(null)
  .catch(null)

const vehicleListItemSchema = z
  .object({
    id: nullableNumberSchema,
    no: z.string().default('').catch(''),
    entityId: z.string().default('').catch(''),
    source: nullableStringSchema,
    externalNo: nullableStringSchema,
    vehiclePartial: vehicleListVehiclePartialSchema,
    vehicleDetail: vehicleListVehicleDetailSchema,
    pickupLocation: nullableUnknownSchema,
    location: nullableUnknownSchema,
    information: vehicleListInformationSchema,
    pricing: vehicleListPricingSchema,
    status: vehicleListStatusSchema,
    vendorInformation: vehicleListVendorInformationSchema,
    isBuyerInvoicePaid: nullableBooleanSchema,
  })
  .passthrough()

const vehicleListPaginationSchema = z
  .object({
    page: nonNegativeCoercedIntSchema,
    limit: positiveCoercedIntSchema,
    offset: nonNegativeCoercedIntSchema,
  })
  .passthrough()
  .optional()

const vehicleListResponseSchema = z
  .object({
    data: z.array(vehicleListItemSchema).default([]).catch([]),
    totalCount: nonNegativeCoercedIntSchema.optional().default(0).catch(0),
    paginationParams: vehicleListPaginationSchema,
  })
  .passthrough()

const parseVehicleListResponse = (
  payload: unknown,
  schemaLabel: string = 'vehicles/list/light',
): VehicleListResponse => {
  const parsedEnvelope = parseWithSchema(vehicleListResponseSchema, payload, schemaLabel)

  const items = parsedEnvelope.data as Array<VehicleListItem>
  const parsedTotalCount =
    typeof parsedEnvelope.totalCount === 'number' && Number.isFinite(parsedEnvelope.totalCount)
      ? parsedEnvelope.totalCount
      : items.length
  const totalCount = Math.max(parsedTotalCount, items.length)

  return {
    items,
    totalCount,
    paginationParams: parsedEnvelope.paginationParams,
  }
}

const parseVehicleListLightResponse = (payload: unknown): VehicleListResponse =>
  parseVehicleListResponse(payload, 'vehicles/list/light')

export { parseVehicleListResponse, parseVehicleListLightResponse }
